import React, {useState, useEffect} from 'react'
import {Formik} from "formik";
import * as yup from 'yup';
import {SuccessMessage} from "../components/Messages";
import ProgressBar from '../../progress-bar.js';
import {
    disabledFormButtonClass,
    FormSubmissionError,
    RequiredFieldMessage,
    TermsConditionsLink
} from "../components/FormElements";
import CheckboxField from "../components/CheckboxField";
import {
    EmailValidation,
    FirstNameValidation,
    LastNameValidation,
    MobileValidation,
    RecaptchaValidation, TermsAndConditionsValidation
} from "../components/FormValidations";
import InputField from "../components/InputField";
import NumberField from "../components/NumberField";
import {getNominateMatchOptions, handleFormSubmit, kidsTakeoverEndpoint} from "../client-api/ANZAPI";
import RecapthaField from "../components/RecapthaField";
import {removePageClassContent} from "../../script";
import _ from "lodash";
import {updateLinkTrackingFields} from "../components/LinkTracking";
import SelectField from "../components/SelectField";


const FIRST_NAME_FIELD = 'FirstName';
const LAST_NAME_FIELD = 'LastName';
const EMAIL_FIELD = 'Email';
const EMAIL_CONFIRMATION_FIELD = 'EmailConfirmation';
const MOBILE_FIELD = 'Mobile';
const NOMINATE_MATCH_FIELD = 'NominateMatch';
const CONTACT_FIRST_NAME_FIELD = 'ContactFirstName';
const CONTACT_LAST_NAME_FIELD = 'ContactLastName';
const CLUB_FIELD = 'Club';
const NUMBER_ATTENDEES_FIELD = 'NoAttendees';
const AGREE_TC_FIELD = 'AgreeTC';
const CHILD_CONSENT_FIELD = 'ChildConsent';
const SIGNUP_FIELD = 'SignUp';
const RECAPTCHA_FIELD = 'Recaptcha';

const initialValues = {
    [FIRST_NAME_FIELD]: '',
    [LAST_NAME_FIELD]: '',
    [MOBILE_FIELD]: '',
    [EMAIL_FIELD]: '',
    [EMAIL_CONFIRMATION_FIELD]: '',
    [NOMINATE_MATCH_FIELD]: '',
    [CONTACT_FIRST_NAME_FIELD]: '',
    [CONTACT_LAST_NAME_FIELD]: '',
    [CLUB_FIELD]: '',
    [NUMBER_ATTENDEES_FIELD]: '',
    [AGREE_TC_FIELD]: false,
    [CHILD_CONSENT_FIELD]: false,
    [SIGNUP_FIELD]: false,
    [RECAPTCHA_FIELD]: ''
}

const submissionValidation = yup.object().shape({
    [FIRST_NAME_FIELD]: FirstNameValidation,
    [LAST_NAME_FIELD]: LastNameValidation,
    [EMAIL_FIELD]: EmailValidation,
    [EMAIL_CONFIRMATION_FIELD]: yup.string()
        .oneOf([yup.ref(EMAIL_FIELD), null], 'Confirmation email does not match.'),
    [MOBILE_FIELD]: MobileValidation,
    [NOMINATE_MATCH_FIELD]: yup.string().required('Nominate match cannot be left blank.'),
    [CONTACT_FIRST_NAME_FIELD]: yup.string().required('Contact first name cannot be left blank.'),
    [CONTACT_LAST_NAME_FIELD]: yup.string().required('Contact last name cannot be left blank.'),
    [CLUB_FIELD]: yup.string().required('Club or school name cannot be left blank.'),
    [NUMBER_ATTENDEES_FIELD]: yup.number().required('Number of attendees'),
    [AGREE_TC_FIELD]: TermsAndConditionsValidation,
    [RECAPTCHA_FIELD]: RecaptchaValidation,
});

const applicationConfirmationTracking = {
    pageName: 'anzcricketworld:kidstakeover:form:confirmation',
    formName: 'anzcricketworld:kidstakeover',
    formStatus: 'submitted',
    journeyStepName: 'confirmation',
    journeyName: 'competition:cricket_kids_takeover',
    journeyStatus: 'complete'
}

const bottomFormCheckBoxes = [
    {
        name: AGREE_TC_FIELD,
        label: <>I agree to the <a href={"#"} data-toggle="modal" data-target="#TCKidsTakeover">ANZ Kids Takeover Terms
            and Conditions</a></>,
        required: true
    },
    {
        name: CHILD_CONSENT_FIELD,
        label: 'I have consent from child’s parent or legal guardian'
    },
    {
        name: SIGNUP_FIELD,
        label: 'Tick here to sign up to receive news, updates and exclusive offers from ANZ Cricket World'
    },
]


const KidsTakeoverForm = (props) => {
    const [successSubmitted, setSuccessSubmitted] = useState(false);
    const [submissionError, setSubmissionError] = useState(null);
    const [matchOptions, setMatchOptions] = useState([]);

    useEffect(() => {
        ProgressBar()
        getNominateMatchOptions()
            .then((options) => {
                setMatchOptions(options)
            })
    }, [])

    return (
        <>
            {successSubmitted === true ? (
                <SuccessMessage
                    heading={'Nice Work'}
                    message={'Your application has been submitted.'}
                    extraMessage={<>See the <a href={"#"} data-toggle="modal" data-target="#TCKidsTakeover">Terms & Conditions</a> for more information.</>}
                />
            ) : (
                <>
                    <RequiredFieldMessage/>
                    <hr/>
                    <Formik
                        initialValues={{...initialValues}}
                        initialErrors={{...initialValues}}
                        onSubmit={(values, {setSubmitting, resetForm}) => {
                            handleFormSubmit(values, kidsTakeoverEndpoint(props.elementId))
                                .then((res) => {
                                    setSubmitting(false)
                                    if (res.success) {
                                        ProgressBar(2)
                                        resetForm({...initialValues});
                                        setSuccessSubmitted(true);
                                        removePageClassContent('.kids-takeover-content')
                                        updateLinkTrackingFields(applicationConfirmationTracking);
                                        pageView();

                                        return;
                                    }

                                    setSubmissionError(res?.message)
                                    $('html, body').animate({
                                        'scrollTop': document.getElementById("submissionErrorId").offsetTop - 50
                                    }, 500);
                                })
                        }}
                        validationSchema={submissionValidation}
                    >{(formikProps) => (
                        <>
                            {submissionError !== null && (
                                <div id={'submissionErrorId'}>
                                    <FormSubmissionError message={submissionError}/>
                                </div>
                            )}

                            <form onSubmit={formikProps.handleSubmit}>
                                <div className={`form-group`}>
                                    <h3>Member details</h3>
                                    <InputField
                                        id={'firstName'}
                                        label={'First name'}
                                        name={FIRST_NAME_FIELD}
                                        required={true}
                                        maxLength={30}
                                        {...formikProps}
                                    />

                                    <InputField
                                        id={'lastName'}
                                        label={'Last name'}
                                        name={LAST_NAME_FIELD}
                                        required={true}
                                        maxLength={30}
                                        {...formikProps}
                                    />

                                    <InputField
                                        id={'email'}
                                        label={'Email'}
                                        name={EMAIL_FIELD}
                                        required={true}
                                        maxLength={129}
                                        {...formikProps}
                                    />

                                    <InputField
                                        id={'emailConfirm'}
                                        label={'Confirm your email address'}
                                        name={EMAIL_CONFIRMATION_FIELD}
                                        required={true}
                                        maxLength={129}
                                        {...formikProps}
                                    />

                                    <NumberField
                                        name={MOBILE_FIELD}
                                        label={'Mobile number'}
                                        id={'mobile'}
                                        allowNegative={false}
                                        className={'short'}
                                        maxLength={20}
                                        required={true}
                                    />

                                    <SelectField
                                        id={'matchNominate'}
                                        label={'Match you would like to nominate your club for'}
                                        name={NOMINATE_MATCH_FIELD}
                                        options={matchOptions}
                                        selectClass={"short"}
                                        required={true}
                                        onChange={(option) => formikProps.setFieldValue(NOMINATE_MATCH_FIELD, option.value)}
                                        {...formikProps}
                                    />
                                    {formikProps.values[NOMINATE_MATCH_FIELD] && <div>{formikProps.values[NOMINATE_MATCH_FIELD]}</div>}

                                    <hr />

                                    <h3>Club or School Details</h3>
                                    <InputField
                                        id={'contactFirstName'}
                                        label={'Contact first name'}
                                        name={CONTACT_FIRST_NAME_FIELD}
                                        required={true}
                                        maxLength={30}
                                        {...formikProps}
                                    />

                                    <InputField
                                        id={'contactLastName'}
                                        label={'Contact surname'}
                                        name={CONTACT_LAST_NAME_FIELD}
                                        required={true}
                                        maxLength={30}
                                        {...formikProps}
                                    />

                                    <InputField
                                        id={'club'}
                                        label={'Club or school name'}
                                        name={CLUB_FIELD}
                                        required={true}
                                        maxLength={30}
                                        {...formikProps}
                                    />

                                    <NumberField
                                        name={NUMBER_ATTENDEES_FIELD}
                                        label={'Number of players to attend'}
                                        id={'noAttendees'}
                                        allowNegative={false}
                                        className={'short'}
                                        maxLength={20}
                                        required={true}
                                    />
                                    <div className={'sub-message pb-5 short'}>
                                        We require a total of 35 children from your club aged between 9 – 13 years to be eligible for the Kids Takeover
                                    </div>

                                    {bottomFormCheckBoxes?.map((item, idx) => {
                                        return (
                                            <div className={'form-group mt-0'} key={idx}>
                                                <CheckboxField
                                                    name={item.name}
                                                    value={formikProps.values[item.name]}
                                                    label={item.label}
                                                    required={item?.required || false}
                                                />
                                            </div>
                                        )
                                    })}

                                    <div className={'pt-4'}>
                                        <RecapthaField name={RECAPTCHA_FIELD}/>
                                    </div>
                                    <hr/>
                                </div>

                                <div className={'links pt-0'}>
                                    <button
                                        type="submit"
                                        className={`btn btn-primary mr-5 ${disabledFormButtonClass(formikProps)}`}
                                        disabled={formikProps.isSubmitting || !_.isEmpty(formikProps.errors)}
                                    >Enter now
                                    </button>
                                    <TermsConditionsLink target={'TCKidsTakeover'}/>
                                </div>

                                <div className={'sub-message mt-5'}>
                                    <div className={'pb-3'}>
                                        <strong>Privacy Policy</strong>
                                    </div>
                                    <div className={'pb-3'}>We understand the need to safeguard your personal information and respect your rights under the Privacy Act 2020.</div>
                                    <div className={'pb-3'}>The personal information provided by you will be held securely by us and used to facilitate registration of ANZ Cricket World events and, unless you have indicated otherwise, will be used to provide you with information about ANZ Cricket World events or our products and services from time to time.</div>
                                    <div className={'pb-3'}>You may request access to and correction of the personal information held by us by emailing <a href={'mailto:info@anzcricketworld.co.nz'}>info@anzcricketworld.co.nz</a> </div>
                                </div>
                            </form>
                        </>
                    )}
                    </Formik>
                </>
            )}

        </>
    )
}

export default KidsTakeoverForm;
